import {
  IProfile,
  IStudentLicense,
  tClientLicence,
  tClientLicences,
  tOnboardingStep,
  tSocialAuthProvider,
  tStudentIdentityProvider
} from 'common/modules/ReactShared';
import { Selector, createSelector } from '@reduxjs/toolkit';
import sortBy from 'lodash/sortBy';
import { checkIfExpired, howManyDaysLeft } from 'common/modules/ReactShared/utils/dates';
import lessonSelectors from '../lesson/selectors';
import { selectShared } from '../sharedSelectors';
import { tSharedAppState, tSharedStore } from '../types';
import { ILanguageInstance } from './preferences';
import { hasTeamConnection } from './profile/hasTeamConnection';

const selectUser: Selector<tSharedAppState, tSharedStore['user']> = createSelector(
  [selectShared],
  (shared) => shared.user
);

const selectProfile: Selector<tSharedAppState, tSharedStore['user']['profile'] | null> =
  createSelector([selectUser], (user) => user?.profile || null);

const selectPreferences: Selector<tSharedAppState, tSharedStore['user']['preferences']> =
  createSelector([selectUser], (user) => user.preferences);

const selectProfilePreferences: Selector<
  tSharedAppState,
  IProfile['preferences'] | undefined
> = createSelector([selectProfile], (profile) => profile?.preferences);

const selectProfileIsValid: Selector<tSharedAppState, IProfile['valid'] | undefined> =
  createSelector([selectProfile], (profile) => profile?.valid);

const selectProfileTargetLanguages: Selector<
  tSharedAppState,
  IProfile['studentTargetLanguages'] | undefined
> = createSelector([selectProfile], (profile) => profile?.studentTargetLanguages);

const selectProfileCurrentTargetLanguage: Selector<
  tSharedAppState,
  IProfile['studentTargetLanguages'][0] | undefined
> = createSelector([selectProfile], (profile) =>
  profile?.studentTargetLanguages.find(
    (lang) => lang.id === profile.activeStudentTargetLanguageId
  )
);

const selectProfileSortedTargetLanguages: Selector<
  tSharedAppState,
  IProfile['studentTargetLanguages'] | undefined
> = createSelector([selectProfileTargetLanguages], (targetLanguages) =>
  sortBy(targetLanguages, (lang) => lang.languageId)
);

const selectProfileSupportLanguages: Selector<
  tSharedAppState,
  IProfile['studentSupportLanguages'] | undefined
> = createSelector([selectProfile], (profile) => profile?.studentSupportLanguages);

const selectProfileCurrentSupportLanguage: Selector<
  tSharedAppState,
  IProfile['studentSupportLanguages'][0] | undefined
> = createSelector([selectProfile], (profile) =>
  profile?.studentSupportLanguages.find(
    (lang) => lang.languageId === profile.nativeLanguageId
  )
);

const selectProfileSortedSupportLanguages: Selector<
  tSharedAppState,
  IProfile['studentSupportLanguages'] | undefined
> = createSelector([selectProfileSupportLanguages], (supportLanguages) =>
  sortBy(supportLanguages, (lang) => lang.languageId)
);

/**
 * The first-login steps a student still has to go through. A school member is
 * not asked for a target language and level: the backend sets both from their
 * class (team group) and licence. A teacher is not asked either: they sign in
 * to teach, not to learn. A student with no school has nothing to set them
 * from, so they are still asked.
 */
export function onboardingSteps(
  profile: IProfile | null,
  targetLanguage: IProfile['studentTargetLanguages'][0] | undefined
): tOnboardingStep[] {
  if (!profile) return [];

  const onboarding: tOnboardingStep[] = [];
  const belongsToSchool = hasTeamConnection(profile);
  const hasTargetLevel = Boolean(
    targetLanguage?.level && targetLanguage.level !== 'undefined'
  );

  if (!belongsToSchool && !profile.isTeacher && !hasTargetLevel) {
    onboarding.push('TARGET_LANGUAGE');
  }

  if (!profile.nativeLanguageId && !profile.nativeStudentSupportLanguageId) {
    onboarding.push('NATIVE_LANGUAGE');
  }

  return onboarding;
}

const selectOnboarding: Selector<tSharedAppState, tOnboardingStep[]> = createSelector(
  [selectProfile, selectProfileCurrentTargetLanguage],
  onboardingSteps
);

const selectTargetLangs: Selector<tSharedAppState, ILanguageInstance[]> = createSelector(
  [selectPreferences],
  (preferences) => preferences.targetLangs
);

const selectAllLangs: Selector<tSharedAppState, ILanguageInstance[]> = createSelector(
  [selectPreferences],
  (preferences) => preferences.allLangs
);

const selectSortedAllLangs: Selector<tSharedAppState, ILanguageInstance[]> =
  createSelector([selectProfile, selectAllLangs], (profile, allLangs) =>
    sortBy(allLangs, (lang) => (lang.code === profile?.locale ? -1 : +1))
  );

const selectProfileCurrentSupportLanguageInfo: Selector<
  tSharedAppState,
  ILanguageInstance | undefined
> = createSelector([selectProfile, selectAllLangs], (profile, allLangs) => {
  return allLangs.find((lang) => lang.id === profile?.nativeLanguageId);
});

const selectProfileCurrentTargetLanguageInfo: Selector<
  tSharedAppState,
  ILanguageInstance | undefined
> = createSelector(
  [selectProfileCurrentTargetLanguage, selectTargetLangs],
  (currentTargetLanguage, allTargetLangs) => {
    return allTargetLangs.find((lang) => lang.id === currentTargetLanguage?.languageId);
  }
);

const selectProfileLocale: Selector<tSharedAppState, IProfile['locale']> = createSelector(
  [selectProfile],
  (profile) => {
    if (profile) {
      return profile.locale;
    }

    return null;
  }
);

const selectUserLocale: Selector<tSharedAppState, string> = createSelector(
  [selectUser],
  (user) => user.locale
);

const selectProfileLicenses: Selector<tSharedAppState, IStudentLicense[]> =
  createSelector([selectProfile], (profile) => profile?.licenses || []);

function licenceAdapter(licenceFromApi: IStudentLicense | null): tClientLicence {
  if (!licenceFromApi) return null;

  let computed: NonNullable<tClientLicence>['computed'] = { status: 'unlimited' };
  const daysLeft = howManyDaysLeft(licenceFromApi.expiresAt);
  // Not started yet while the start date (midnight) is still in the future.
  if (licenceFromApi.startDate && !checkIfExpired(licenceFromApi.startDate)) {
    computed = {
      status: 'future',
      daysUntilStart: Math.max(howManyDaysLeft(licenceFromApi.startDate) ?? 0, 0)
    };
  } else if (daysLeft === null) {
    computed = { status: 'unlimited' };
  } else if (daysLeft >= 0) {
    computed = { status: 'active', daysLeft };
  } else {
    computed = { status: 'inactive', expired: true };
  }
  return {
    id: licenceFromApi.id,
    startDate: licenceFromApi.startDate ?? null,
    expiresAt: licenceFromApi.expiresAt,
    name: licenceFromApi.plan.name || licenceFromApi.plan.systemName,
    nameTranslation:
      Object.values(licenceFromApi.plan.nameTranslations || {})[0] ||
      licenceFromApi.plan.name ||
      licenceFromApi.plan.systemName,
    imageURL: licenceFromApi.team.brand,
    allowsAssignment: licenceFromApi.assignments,
    courses: licenceFromApi.courses || [],
    teamStudentId: licenceFromApi.teamStudent.id,
    teamPrivacyURL: licenceFromApi.team.privacyURL,
    teamContactEmail: licenceFromApi.team.contactEmail,
    // @ts-expect-error
    teamImageURL: licenceFromApi.team.imageURL,
    teamName: licenceFromApi.team.name,
    computed,
    teamGroups: licenceFromApi.teamGroups || [],
    program: licenceFromApi.program || null,
    boardsEnabled: licenceFromApi.boardsEnabled
  };
}

const selectCurrentLicence: Selector<tSharedAppState, tClientLicence> = createSelector(
  [selectProfile],
  (profile) => {
    const activeLicense = profile?.activeLicense;
    const licenses = profile?.licenses;
    if (activeLicense && licenses?.length) {
      const licenseData = licenses.find((l) => l.id === activeLicense.id);
      if (licenseData) {
        return licenceAdapter(licenseData);
      }
    }
    return null;
  }
);

const selectLightAllLicences: Selector<tSharedAppState, tClientLicences> = createSelector(
  [selectProfile],
  (profile) => {
    const licenses = profile?.licenses || [];
    return licenses.map(licenceAdapter).filter(Boolean) as NonNullable<tClientLicence>[];
  }
);
const selectLightActiveLicences: Selector<tSharedAppState, tClientLicences> =
  createSelector([selectLightAllLicences], (allLicences) => {
    return allLicences.filter((item) => {
      const { computed } = item;
      return /^(active|unlimited)/.test(computed.status);
    });
  });

// Licenses with a future start date: connected to a program, but not started yet.
const selectLightFutureLicences: Selector<tSharedAppState, tClientLicences> =
  createSelector([selectLightAllLicences], (allLicences) => {
    return allLicences.filter((item) => item.computed.status === 'future');
  });

const selectLightActiveLicencesWithRecentlyExpired: Selector<
  tSharedAppState,
  tClientLicences
> = createSelector([selectLightAllLicences], (allLicences) => {
  return allLicences.filter((item) => {
    const { computed } = item;
    if (/^(active|unlimited|future)/.test(computed.status)) return true;

    const daysLeft = howManyDaysLeft(item.expiresAt);
    return daysLeft !== null ? daysLeft >= -30 : false;
  });
});

const IDENTITY_PROVIDER_TO_AUTH_PROVIDER_MAP: Record<
  tStudentIdentityProvider,
  tSocialAuthProvider
> = {
  google_student: 'google',
  facebook_student: 'facebook',
  microsoft_student: 'microsoft',
  apple_student: 'apple',
  feide_student: 'feide'
};

const selectConnectedAuthProviders: Selector<
  tSharedAppState,
  { provider: tSocialAuthProvider }[] | undefined
> = createSelector([selectProfile], (profile) => {
  return profile?.studentIdentities?.map((identity) => ({
    provider: IDENTITY_PROVIDER_TO_AUTH_PROVIDER_MAP[identity.provider]
  }));
});

const selectIfSupportEqualsTargetLanguage: Selector<tSharedAppState, boolean> =
  createSelector(
    [
      selectProfileCurrentTargetLanguage,
      selectProfileCurrentSupportLanguage,
      lessonSelectors.selectLesson
    ],
    (currentTargetLanguage, currentSupportLanguage, currentLesson) => {
      if (currentLesson && currentLesson.language) {
        return currentLesson.language.id === currentSupportLanguage?.languageId;
      }
      if (currentTargetLanguage && currentSupportLanguage) {
        return currentTargetLanguage.languageId === currentSupportLanguage.languageId;
      }

      return false;
    }
  );

const selectLearningProgress: Selector<tSharedAppState, IProfile['learningProgress']> =
  createSelector([selectProfile], (profile) => {
    return profile?.learningProgress;
  });

const selectNewLearningProgress: Selector<
  tSharedAppState,
  IProfile['newLearningProgress']
> = createSelector([selectProfile], (profile) => {
  return profile?.newLearningProgress;
});

const selectProfileStats: Selector<tSharedAppState, IProfile['stats']> = createSelector(
  [selectProfile],
  (profile) => {
    return profile?.stats;
  }
);

const userSharedSelectors = {
  selectUser,
  selectUserLocale,
  selectProfile,
  selectProfilePreferences,
  selectProfileIsValid,
  selectProfileTargetLanguages,
  selectProfileCurrentTargetLanguage,
  selectProfileSortedTargetLanguages,
  selectProfileCurrentSupportLanguage,
  selectProfileSupportLanguages,
  selectProfileSortedSupportLanguages,
  selectOnboarding,
  selectTargetLangs,
  selectSortedAllLangs,
  selectAllLangs,
  selectProfileCurrentSupportLanguageInfo,
  selectProfileCurrentTargetLanguageInfo,
  selectProfileLocale,
  selectConnectedAuthProviders,
  selectProfileLicenses,
  selectIfSupportEqualsTargetLanguage,
  selectCurrentLicence,
  selectLightActiveLicences,
  selectLightAllLicences,
  selectLightFutureLicences,
  selectLightActiveLicencesWithRecentlyExpired,
  selectLearningProgress,
  selectNewLearningProgress,
  selectProfileStats
};

export default userSharedSelectors;
